import { StandardEvents } from '@shopify/events';

/**
 * CrafTech: полето за гравиране (snippets/ct-personalization.liquid).
 *
 * Прави две неща: брои знаците и се пре-рендира при смяна на вариант.
 * Второто е нужно, защото Horizon morph-ва .product-form-buttons САМО когато
 * се сменят количествените правила (assets/product-form.js:874), тоест при
 * обикновена смяна „Не“ -> „Да“ полето не би се появило.
 */
class CtPersonalizationComponent extends HTMLElement {
  #target = null;

  connectedCallback() {
    this.addEventListener('input', this.#onInput);
    // Браузърът само фокусира първото невалидно поле (на мобилно без балонче) - клиентът
    // вижда, че „нищо не стана“. `invalid` се вдига при всеки път на добавяне (бутон,
    // sticky бар, quick add), затова съобщението се показва оттук, не от бутона.
    this.addEventListener('invalid', this.#onInvalid, true);
    this.#syncCounters();
    // Мобилният quick add (assets/quick-add.js, isMobileBreakpoint) маха .product-details
    // заедно с обвивката #ProductInformation-…, затова #quick-add-modal-content е в списъка -
    // без него на мобилно closest() е null, productSelect не се слуша и полето не се появява
    // при „Не“ -> „Да“ (мерено 08.09.2026: вариант „Да“ в количката с properties {}).
    this.#target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], #quick-add-modal-content, product-card');
    this.#target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#syncRequireAny();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('invalid', this.#onInvalid, true);
    this.#target?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  #onInput = () => {
    this.#syncCounters();
    this.#syncRequireAny();
    if ([...this.querySelectorAll('.ct-pers__input')].every((i) => i.checkValidity())) this.#notice(false);
  };

  #onInvalid = (event) => {
    this.#notice(true);
    // Браузърът фокусира полето, но на мобилно клавиатурата го покрива. Слагаме го
    // малко под горния ръб на видимата част (под sticky header-а); в quick add модала
    // скролът е на самия диалог, затова там е scrollIntoView.
    const input = event.target;
    if (!(input instanceof HTMLElement) || input !== this.querySelector('.ct-pers__input:invalid')) return;
    setTimeout(() => {
      const dialog = input.closest('dialog');
      if (dialog) {
        this.#keepSheetAboveKeyboard(dialog, input);
        input.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }
      const header = document.querySelector('#header-component, header');
      const offset = (header ? header.getBoundingClientRect().height : 0) + 24;
      const label = input.closest('.ct-pers__field') || input;
      window.scrollTo({ top: label.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
    }, 80);
  };

  /**
   * Quick add на мобилно е bottom sheet с fixed позиция и височина по съдържанието; при отворена
   * клавиатура Chrome (Android) свива само визуалния viewport и листът остава под клавиатурата.
   * Докато полето е на фокус: вдигаме листа с височината на клавиатурата и ограничаваме
   * съдържанието му до видимата височина (то е overflow-y: auto), после скролваме до полето.
   * При затваряне на клавиатурата всичко се връща. Без visualViewport (стар браузър) - нищо.
   */
  #keepSheetAboveKeyboard(dialog, input) {
    const vv = window.visualViewport;
    const content = dialog.querySelector('#quick-add-modal-content') || dialog.firstElementChild;
    if (!vv || !content) return;
    const apply = () => {
      const keyboard = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      if (keyboard < 80 || document.activeElement !== input) {
        dialog.style.removeProperty('transform');
        content.style.removeProperty('max-height');
        return;
      }
      dialog.style.transform = `translateY(-${keyboard}px)`;
      content.style.maxHeight = `${vv.height - 48}px`;
      input.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };
    const stop = () => {
      vv.removeEventListener('resize', apply);
      input.removeEventListener('blur', stop);
      dialog.style.removeProperty('transform');
      content.style.removeProperty('max-height');
    };
    vv.addEventListener('resize', apply);
    input.addEventListener('blur', stop, { once: true });
    apply();
  }

  /** Показва/скрива „Продуктът още не е добавен…“ (snippet-ът рендира .ct-pers__notice). */
  #notice(show) {
    const el = this.querySelector('.ct-pers__notice');
    if (!el) return;
    el.hidden = !show;
    this.classList.toggle('ct-pers--invalid', show);
  }

  /**
   * „Поне едно поле“: продукт с няколко незадължителни полета (стойката за бижута),
   * при който вариантът „Да“ без нито един текст е грешка на клиента. Слага се
   * custom validity на първото поле, така че checkValidity() на формата пада и
   * браузърът показва съобщението от data-require-any-msg; попълни ли се което и да е
   * поле, се вдига. Отметките не се броят - те не носят текст за гравиране.
   */
  #syncRequireAny() {
    const msg = this.dataset.requireAnyMsg;
    if (!msg) return;
    const inputs = [...this.querySelectorAll('.ct-pers__input')];
    if (!inputs.length) return;
    const filled = inputs.some((i) => i.value.trim().length > 0);
    inputs[0].setCustomValidity(filled ? '' : msg);
  }

  /**
   * Броячът е за ПОЛЕ, не за компонент: наборът е конфигурируем (продуктовото
   * метаполе custom.pers_fields) и всяко поле може да има свой `max`. Затова
   * се чете data-max на .ct-pers__field, а data-max на компонента остава
   * резервата. Отметките нямат .ct-pers__input и брояч - за тях цикълът мълчи.
   */
  #syncCounters() {
    const fallback = Number(this.dataset.max) || 25;
    for (const field of this.querySelectorAll('.ct-pers__field')) {
      const input = field.querySelector('.ct-pers__input');
      const counter = field.querySelector('[data-ct-counter]');
      if (!input || !counter) continue;
      const max = Number(field.dataset.max) || Number(input.getAttribute('maxlength')) || fallback;
      counter.textContent = `${input.value.length}/${max}`;
    }
  }

  #onProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const next = detail?.html?.querySelector('ct-personalization-component');
        if (!next) return;
        this.dataset.max = next.dataset.max;
        this.innerHTML = next.innerHTML;
        if (next.dataset.requireAnyMsg) this.dataset.requireAnyMsg = next.dataset.requireAnyMsg;
        else delete this.dataset.requireAnyMsg;
        this.#syncCounters();
        this.#syncRequireAny();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ct-personalization] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('ct-personalization-component')) {
  customElements.define('ct-personalization-component', CtPersonalizationComponent);
}
